import React, { useMemo } from "react";
import DropDownSelector from "../../DropdownSelector";
import {
  Auction,
  AuctionKind,
  AuctionKindAscending,
  AuctionKindBidding,
  AuctionKindGDAContinuous,
  AuctionKindGDADiscrete,
  AuctionKindJapanese,
  AuctionKindSealed,
  AuctionKindSealedFloor,
} from "../../../types";
import PriceDisplay from "../../PriceDisplay";
import CountdownTimer from "../../CountdownTimer";
import ItemCount from "../../ItemCount";
import Button from "../../Button";
import Bid from "../../Bid";
import { AuctionBaseCard } from "./Card";
import ButtonHold from "../../Button/Hold";
import Buy from "../../Buy";

interface CTAProps {
  kind: AuctionKind;
  price: number;
  onBidClick: (price: number, amount: number) => void;
  statefulPurchaseQuantity: number;
  placementNote?: string;
  activeBid?: number;
}

const CTA = ({
  kind,
  price,
  onBidClick,
  statefulPurchaseQuantity,
  placementNote,
  activeBid,
}: CTAProps) => {
  switch (kind) {
    case AuctionKindAscending: {
      return (
        <Bid
          minBid={0}
          onClick={onBidClick}
          placementNote={placementNote}
          activeBid={!!activeBid}
        />
      );
    }
    case AuctionKindSealed: {
      return (
        <Bid
          minBid={0}
          onClick={onBidClick}
          placementNote={placementNote}
          activeBid={!!activeBid}
        />
      );
    }
    case AuctionKindSealedFloor: {
      return (
        <Bid
          minBid={0}
          onClick={onBidClick}
          placementNote={placementNote}
          activeBid={!!activeBid}
        />
      );
    }
    case AuctionKindJapanese: {
      return (
        <ButtonHold
          label="HOLD THIS BUTTON TO STAY IN"
          labelStarting="JOINING..."
          labelHolding="HOLDING..."
        />
      );
    }
  }

  return (
    <Buy
      onClick={() => {
        onBidClick(price, statefulPurchaseQuantity);
      }}
    />
  );
};

export interface AuctionBaseProps {
  auction?: Auction;
  onBidClick?: (price: number, amount: number) => void;
  traffic?: number;
  currentPrice?: number;
  targetDate?: number;
  quantity?: number;
  sold?: number;
  statefulPurchaseQuantity?: number;
  setStatefulPurchaseQuantity?: (quantity: number) => void;
  setVariant?: (variant: number) => void;
  placementNote?: string;
  priceRising?: boolean;
  stage?: "starting" | "ended";
  activeBid?: number;
}
export const AuctionBase: React.FC<AuctionBaseProps> = ({
  auction,
  onBidClick,
  traffic,
  currentPrice,
  targetDate,
  quantity,
  sold,
  statefulPurchaseQuantity,
  setStatefulPurchaseQuantity,
  setVariant,
  placementNote,
  priceRising,
  stage,
  activeBid,
}) => {
  const priceDisplayLabel = (() => {
    if (!auction) return undefined;

    switch (auction.kind) {
      case AuctionKindSealedFloor: {
        return "current floor price";
      }
      default: {
        return undefined;
      }
    }
  })();

  const isSoldOut = useMemo(() => {
    if (!!quantity && !!sold && sold >= quantity) return true;
    return false;
  }, [quantity, sold]);

  return (
    <>
      {!auction && !!stage && stage === "starting" && (
        <>
          {!!targetDate && (
            <CountdownTimer label="drop begins in" targetDate={targetDate} />
          )}
        </>
      )}
      {!auction && !!stage && stage === "ended" && (
        <>
          <p className="text-center text-[34px] font-bold mt-[59px] mb-[59px]">
            auction has ended
          </p>
        </>
      )}
      {!!auction && (
        <>
          {isSoldOut && (
            <>
              <p className="text-center text-[34px] font-bold mt-[59px] mb-[59px]">
                sold out
              </p>
            </>
          )}
          {!isSoldOut && (
            <>
              {currentPrice !== undefined && (
                <>
                  {priceRising !== undefined ? (
                    <PriceDisplay
                      label={priceDisplayLabel}
                      price={Number(currentPrice.toFixed(2))}
                      containerClassName="mt-[67px]"
                      variant="fluctuating"
                      isRising={priceRising}
                    />
                  ) : (
                    <PriceDisplay
                      label={priceDisplayLabel}
                      price={Number(currentPrice.toFixed(2))}
                      containerClassName="mt-[67px]"
                    />
                  )}
                </>
              )}
              {!!targetDate && (
                <CountdownTimer label="time left" targetDate={targetDate} />
              )}
              {quantity != undefined && sold != undefined && (
                <ItemCount stock={quantity - sold} initialStock={quantity} />
              )}
              {!!activeBid && (
                <PriceDisplay
                  label={"current bid"}
                  price={Number(activeBid.toFixed(2))}
                  containerClassName="mt-[34px]"
                />
              )}

              {[AuctionKindGDAContinuous, AuctionKindGDADiscrete].includes(
                auction.kind
              ) && (
                <div className=" mb-[25px] flex flex-row items-center justify-center mt-[67px] gap-x-[20px]">
                  <p className="font-bold text-[24px]">Items</p>
                  <div className="   rounded-lg border border-[#BFBFBF] flex flex-row items-center px-5 py-3 w-full justify-between relative bg-white">
                    <form>
                      <input
                        type="number"
                        className="w-full  text-2xl outline-none ring-0 focus:outline-none placeholder:text-[#ABABAB]"
                        value={statefulPurchaseQuantity}
                        onChange={(e) => {
                          setStatefulPurchaseQuantity(e.target.valueAsNumber);
                        }}
                      />
                    </form>
                  </div>
                </div>
              )}
              <>
                <div className="mt-[20px]">
                  <CTA
                    kind={auction.kind}
                    price={currentPrice}
                    onBidClick={onBidClick}
                    statefulPurchaseQuantity={statefulPurchaseQuantity}
                    placementNote={placementNote}
                    activeBid={activeBid}
                  />
                  <p className="m-[10px] text-[18px] text-auctionMid text-center">{`${
                    traffic ?? 0
                  } people on this page`}</p>
                </div>
              </>
            </>
          )}
        </>
      )}
    </>
  );
};

export default AuctionBase;
